// This file is auto-generated by @hey-api/openapi-ts

import type {
  Options as ClientOptions,
  TDataShape,
  Client,
} from "@hey-api/client-fetch";
import type {
  GetJwksData,
  GetJwksResponse,
  IssueLicenseData,
  IssueLicenseResponse,
  IssueLicenseError,
  ListControllersData,
  ListControllersResponse,
  CreateControllerData,
  CreateControllerResponse,
  CreateControllerError,
  ListUnclaimedControllersData,
  ListUnclaimedControllersResponse,
  ClaimControllerData,
  ClaimControllerResponse,
  ClaimControllerError,
  UnclaimControllerData,
  UnclaimControllerResponse,
  UnclaimControllerError,
  UpdateControllerStaticIpData,
  UpdateControllerStaticIpResponse,
  UpdateControllerStaticIpError,
  SetControllerVersionData,
  SetControllerVersionResponse,
  SetControllerVersionError,
  ReloadControllerConfigurationData,
  ReloadControllerConfigurationResponse,
  ReloadControllerConfigurationError,
  RestartControllerData,
  RestartControllerResponse,
  RestartControllerError,
  ReloadAndRestartControllerData,
  ReloadAndRestartControllerResponse,
  ReloadAndRestartControllerError,
  SetControllerLogLevelData,
  SetControllerLogLevelResponse,
  SetControllerLogLevelError,
  GetControllerSnapshotData,
  GetControllerSnapshotError,
  GetControllerSnapshotHistoryData,
  GetControllerSnapshotHistoryError,
  GetControllerSnapshotAtAgeData,
  GetControllerSnapshotAtAgeError,
  ClaimPartnerControllerData,
  ClaimPartnerControllerResponse,
  ClaimPartnerControllerError,
  IngestControllerVersionsFromMqttData,
  IngestControllerVersionsFromMqttError,
  GetControllerForSiteData,
  GetControllerForSiteResponse,
  GetControllerForSiteError,
  GetLivePowerForSiteData,
  GetLivePowerForSiteResponse,
  GetLivePowerForSiteError,
} from "./types.gen";
import { client as _heyApiClient } from "./client.gen";

export type Options<
  TData extends TDataShape = TDataShape,
  ThrowOnError extends boolean = boolean,
> = ClientOptions<TData, ThrowOnError> & {
  /**
   * You can provide a client instance returned by `createClient()` instead of
   * individual options. This might be also useful if you want to implement a
   * custom client.
   */
  client?: Client;
  /**
   * You can pass arbitrary values through the `meta` object. This can be
   * used to access values that aren't defined as part of the SDK function.
   */
  meta?: Record<string, unknown>;
};

/**
 * Licensing JSON Web Key Set
 * Public keys used to verify licenses issued by this service.
 */
export const getJwks = <ThrowOnError extends boolean = false>(
  options?: Options<GetJwksData, ThrowOnError>,
) => {
  return (options?.client ?? _heyApiClient).get<
    GetJwksResponse,
    unknown,
    ThrowOnError
  >({
    url: "/.well-known/jwks.json",
    ...options,
  });
};

/**
 * Issue a license
 * Issue a signed JWT license for a site/serial number.
 */
export const issueLicense = <ThrowOnError extends boolean = false>(
  options: Options<IssueLicenseData, ThrowOnError>,
) => {
  return (options.client ?? _heyApiClient).post<
    IssueLicenseResponse,
    IssueLicenseError,
    ThrowOnError
  >({
    security: [
      {
        scheme: "bearer",
        type: "http",
      },
      {
        scheme: "bearer",
        type: "http",
      },
    ],
    url: "/v1/licenses/",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options?.headers,
    },
  });
};

/**
 * List all controllers
 * Returns every registered controller, whether claimed by a site or still unassigned.
 */
export const listControllers = <ThrowOnError extends boolean = false>(
  options?: Options<ListControllersData, ThrowOnError>,
) => {
  return (options?.client ?? _heyApiClient).get<
    ListControllersResponse,
    unknown,
    ThrowOnError
  >({
    security: [
      {
        scheme: "bearer",
        type: "http",
      },
      {
        scheme: "bearer",
        type: "http",
      },
      {
        scheme: "bearer",
        type: "http",
      },
    ],
    url: "/v1/controllers/",
    ...options,
  });
};

/**
 * Create a controller
 * Register a new controller. The controller starts unassigned.
 */
export const createController = <ThrowOnError extends boolean = false>(
  options: Options<CreateControllerData, ThrowOnError>,
) => {
  return (options.client ?? _heyApiClient).post<
    CreateControllerResponse,
    CreateControllerError,
    ThrowOnError
  >({
    security: [
      {
        scheme: "bearer",
        type: "http",
      },
      {
        scheme: "bearer",
        type: "http",
      },
      {
        scheme: "bearer",
        type: "http",
      },
    ],
    url: "/v1/controllers/",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options?.headers,
    },
  });
};

/**
 * List unclaimed controllers
 * Returns every registered controller that is not yet claimed by a site.
 */
export const listUnclaimedControllers = <ThrowOnError extends boolean = false>(
  options?: Options<ListUnclaimedControllersData, ThrowOnError>,
) => {
  return (options?.client ?? _heyApiClient).get<
    ListUnclaimedControllersResponse,
    unknown,
    ThrowOnError
  >({
    security: [
      {
        scheme: "bearer",
        type: "http",
      },
      {
        scheme: "bearer",
        type: "http",
      },
      {
        scheme: "bearer",
        type: "http",
      },
    ],
    url: "/v1/controllers/unclaimed",
    ...options,
  });
};

/**
 * Claim a controller for a site
 */
export const claimController = <ThrowOnError extends boolean = false>(
  options: Options<ClaimControllerData, ThrowOnError>,
) => {
  return (options.client ?? _heyApiClient).post<
    ClaimControllerResponse,
    ClaimControllerError,
    ThrowOnError
  >({
    security: [
      {
        scheme: "bearer",
        type: "http",
      },
      {
        scheme: "bearer",
        type: "http",
      },
      {
        scheme: "bearer",
        type: "http",
      },
    ],
    url: "/v1/controllers/{controller_id}/claim",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options?.headers,
    },
  });
};

/**
 * Unclaim a controller from a site
 * Clears the controller's partner and site, so it is no longer deployed to. The controller is only unclaimed when it is currently claimed by the given site_id; otherwise the request is rejected with a 409. A device that is already running keeps its license and credentials until it is redeployed or reset.
 */
export const unclaimController = <ThrowOnError extends boolean = false>(
  options: Options<UnclaimControllerData, ThrowOnError>,
) => {
  return (options.client ?? _heyApiClient).post<
    UnclaimControllerResponse,
    UnclaimControllerError,
    ThrowOnError
  >({
    security: [
      {
        scheme: "bearer",
        type: "http",
      },
      {
        scheme: "bearer",
        type: "http",
      },
      {
        scheme: "bearer",
        type: "http",
      },
    ],
    url: "/v1/controllers/{controller_id}/unclaim",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options?.headers,
    },
  });
};

/**
 * Configure a controller's static IP
 * Sets the static IP configuration for a controller. Passing a null static_ip removes the configuration so the controller falls back to DHCP.
 */
export const updateControllerStaticIp = <ThrowOnError extends boolean = false>(
  options: Options<UpdateControllerStaticIpData, ThrowOnError>,
) => {
  return (options.client ?? _heyApiClient).patch<
    UpdateControllerStaticIpResponse,
    UpdateControllerStaticIpError,
    ThrowOnError
  >({
    security: [
      {
        scheme: "bearer",
        type: "http",
      },
      {
        scheme: "bearer",
        type: "http",
      },
      {
        scheme: "bearer",
        type: "http",
      },
    ],
    url: "/v1/controllers/{controller_id}/static-ip",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options?.headers,
    },
  });
};

/**
 * Set a controller's target version
 * Sets the semver version a controller will run on its next deploy. The update is rejected with a 409 when the new version is older than the current target version, unless allow_backwards is true.
 */
export const setControllerVersion = <ThrowOnError extends boolean = false>(
  options: Options<SetControllerVersionData, ThrowOnError>,
) => {
  return (options.client ?? _heyApiClient).patch<
    SetControllerVersionResponse,
    SetControllerVersionError,
    ThrowOnError
  >({
    security: [
      {
        scheme: "bearer",
        type: "http",
      },
      {
        scheme: "bearer",
        type: "http",
      },
      {
        scheme: "bearer",
        type: "http",
      },
    ],
    url: "/v1/controllers/{controller_id}/version",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options?.headers,
    },
  });
};

/**
 * Reload a controller's configuration
 * Tells the controller to fetch the latest configuration from the Pleevi API and stage it. The new configuration takes effect on the controller's next restart.
 */
export const reloadControllerConfiguration = <
  ThrowOnError extends boolean = false,
>(
  options: Options<ReloadControllerConfigurationData, ThrowOnError>,
) => {
  return (options.client ?? _heyApiClient).post<
    ReloadControllerConfigurationResponse,
    ReloadControllerConfigurationError,
    ThrowOnError
  >({
    security: [
      {
        scheme: "bearer",
        type: "http",
      },
      {
        scheme: "bearer",
        type: "http",
      },
      {
        scheme: "bearer",
        type: "http",
      },
    ],
    url: "/v1/controllers/{controller_id}/reload-configuration",
    ...options,
  });
};

/**
 * Restart a controller
 * Restarts the controller process so it picks up any staged configuration.
 */
export const restartController = <ThrowOnError extends boolean = false>(
  options: Options<RestartControllerData, ThrowOnError>,
) => {
  return (options.client ?? _heyApiClient).post<
    RestartControllerResponse,
    RestartControllerError,
    ThrowOnError
  >({
    security: [
      {
        scheme: "bearer",
        type: "http",
      },
      {
        scheme: "bearer",
        type: "http",
      },
      {
        scheme: "bearer",
        type: "http",
      },
    ],
    url: "/v1/controllers/{controller_id}/restart",
    ...options,
  });
};

/**
 * Reload configuration and restart a controller
 * Fetches and stages the latest configuration, then restarts the controller to apply it.
 */
export const reloadAndRestartController = <
  ThrowOnError extends boolean = false,
>(
  options: Options<ReloadAndRestartControllerData, ThrowOnError>,
) => {
  return (options.client ?? _heyApiClient).post<
    ReloadAndRestartControllerResponse,
    ReloadAndRestartControllerError,
    ThrowOnError
  >({
    security: [
      {
        scheme: "bearer",
        type: "http",
      },
      {
        scheme: "bearer",
        type: "http",
      },
      {
        scheme: "bearer",
        type: "http",
      },
    ],
    url: "/v1/controllers/{controller_id}/reload-and-restart",
    ...options,
  });
};

/**
 * Set a controller's log level at runtime
 * Swaps the controller's tracing filter at runtime. The override reverts after 15 minutes.
 */
export const setControllerLogLevel = <ThrowOnError extends boolean = false>(
  options: Options<SetControllerLogLevelData, ThrowOnError>,
) => {
  return (options.client ?? _heyApiClient).post<
    SetControllerLogLevelResponse,
    SetControllerLogLevelError,
    ThrowOnError
  >({
    security: [
      {
        scheme: "bearer",
        type: "http",
      },
      {
        scheme: "bearer",
        type: "http",
      },
      {
        scheme: "bearer",
        type: "http",
      },
    ],
    url: "/v1/controllers/{controller_id}/log-level",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options?.headers,
    },
  });
};

/**
 * Capture a controller snapshot now
 * Captures the controller's live situation on demand and returns it as a snapshot. The JSON snapshot is the response body.
 */
export const getControllerSnapshot = <ThrowOnError extends boolean = false>(
  options: Options<GetControllerSnapshotData, ThrowOnError>,
) => {
  return (options.client ?? _heyApiClient).get<
    unknown,
    GetControllerSnapshotError,
    ThrowOnError
  >({
    security: [
      {
        scheme: "bearer",
        type: "http",
      },
      {
        scheme: "bearer",
        type: "http",
      },
      {
        scheme: "bearer",
        type: "http",
      },
    ],
    url: "/v1/controllers/{controller_id}/snapshot",
    ...options,
  });
};

/**
 * Export a controller's full snapshot history
 * Returns every snapshot the controller currently retains in memory (a rolling, tiered window of up to ~6 hours), oldest first, as a JSON array. The response is streamed: it routinely runs to tens of megabytes.
 */
export const getControllerSnapshotHistory = <
  ThrowOnError extends boolean = false,
>(
  options: Options<GetControllerSnapshotHistoryData, ThrowOnError>,
) => {
  return (options.client ?? _heyApiClient).get<
    unknown,
    GetControllerSnapshotHistoryError,
    ThrowOnError
  >({
    security: [
      {
        scheme: "bearer",
        type: "http",
      },
      {
        scheme: "bearer",
        type: "http",
      },
      {
        scheme: "bearer",
        type: "http",
      },
    ],
    url: "/v1/controllers/{controller_id}/snapshot/history",
    ...options,
  });
};

/**
 * Get a controller snapshot from a point in history
 * Returns the retained snapshot closest to a point in the past. The age is an ISO 8601 duration expressing how long ago, e.g. PT15M or PT1H.
 */
export const getControllerSnapshotAtAge = <
  ThrowOnError extends boolean = false,
>(
  options: Options<GetControllerSnapshotAtAgeData, ThrowOnError>,
) => {
  return (options.client ?? _heyApiClient).get<
    unknown,
    GetControllerSnapshotAtAgeError,
    ThrowOnError
  >({
    security: [
      {
        scheme: "bearer",
        type: "http",
      },
      {
        scheme: "bearer",
        type: "http",
      },
      {
        scheme: "bearer",
        type: "http",
      },
    ],
    url: "/v1/controllers/{controller_id}/snapshot/history/{age}",
    ...options,
  });
};

/**
 * Connect a Pleevi Edge device to a site
 * Connects the Pleevi Edge device with this hostname to one of the authenticated partner's sites. Claiming a device this partner already connected to the same site succeeds again; a device connected to any other site is rejected with a 409.
 */
export const claimPartnerController = <ThrowOnError extends boolean = false>(
  options: Options<ClaimPartnerControllerData, ThrowOnError>,
) => {
  return (options.client ?? _heyApiClient).post<
    ClaimPartnerControllerResponse,
    ClaimPartnerControllerError,
    ThrowOnError
  >({
    security: [
      {
        scheme: "bearer",
        type: "http",
      },
      {
        scheme: "bearer",
        type: "http",
      },
    ],
    url: "/v1/partner/controllers/{hostname}/claim",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options?.headers,
    },
  });
};

/**
 * Record the versions controllers published over MQTT
 * Forwarded by telegraf from the v2/{partner_id}/{site_id}/controller/{controller_id}/version topic. Records the version a controller reports it is running as its current_version, so it can be compared against target_version.
 */
export const ingestControllerVersionsFromMqtt = <
  ThrowOnError extends boolean = false,
>(
  options: Options<IngestControllerVersionsFromMqttData, ThrowOnError>,
) => {
  return (options.client ?? _heyApiClient).post<
    unknown,
    IngestControllerVersionsFromMqttError,
    ThrowOnError
  >({
    url: "/v1/controller/mqtt/versions",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options?.headers,
    },
  });
};

/**
 * The controller claimed for a site
 * The Pleevi Edge device connected to this site, when there is one. The dashboard uses this to decide whether live views are available for the site.
 */
export const getControllerForSite = <ThrowOnError extends boolean = false>(
  options: Options<GetControllerForSiteData, ThrowOnError>,
) => {
  return (options.client ?? _heyApiClient).get<
    GetControllerForSiteResponse,
    GetControllerForSiteError,
    ThrowOnError
  >({
    security: [
      {
        scheme: "bearer",
        type: "http",
      },
      {
        scheme: "bearer",
        type: "http",
      },
      {
        scheme: "bearer",
        type: "http",
      },
    ],
    url: "/v1/sites/{site_id}/controller",
    ...options,
  });
};

/**
 * Live active power for a site's controller
 * Per-asset active power in watts at one-second resolution, read from the Prometheus server on the site's controller. Covers at most the last hour; pass start to fetch only the tail since data already held.
 */
export const getLivePowerForSite = <ThrowOnError extends boolean = false>(
  options: Options<GetLivePowerForSiteData, ThrowOnError>,
) => {
  return (options.client ?? _heyApiClient).get<
    GetLivePowerForSiteResponse,
    GetLivePowerForSiteError,
    ThrowOnError
  >({
    security: [
      {
        scheme: "bearer",
        type: "http",
      },
      {
        scheme: "bearer",
        type: "http",
      },
      {
        scheme: "bearer",
        type: "http",
      },
    ],
    url: "/v1/sites/{site_id}/live-power",
    ...options,
  });
};
